import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Salad, Printer, Sparkles, Beaker, BookOpen, X, MessageSquare, ShieldAlert } from "lucide-react";
import { DIET_TYPES, ACTIVITY_LEVELS, computeMetabolicTargets } from "../lib/dietTypes";
import AdvancedDietChart from "../components/AdvancedDietChart";
import { autoDetectConditions } from "../lib/conditionMapper";

// Diet Hub — two tabs:
//   - Planner: patient vitals + preferences → AI meal plan
//   - Debugger: 5 diet cards + AI Masterclass
export default function DietHub() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [patient, setPatient] = useState(null);
  const [tab, setTab] = useState("planner");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    // Support deep-linking via ?tab=advanced (used by v1 Print's "Upgrade" button).
    const t = searchParams.get("tab");
    if (t === "advanced" || t === "debugger" || t === "planner") setTab(t);
  }, [searchParams]);
  const [lang, setLang] = useState("mr"); // en | mr — default Marathi for clinic patients
  const [profile, setProfile] = useState({ age: "", weight_kg: "", height_cm: "", waist_cm: "", sex: "" });
  const [prefs, setPrefs] = useState({
    activity_id: "sedentary",
    diet_type: "dikshit",
    choice: "Vegetarian",
    likings: "",
    allergies: "",
    macros: { carb: 40, protein: 30, fat: 30 },
  });
  const [planText, setPlanText] = useState("");
  const [planId, setPlanId] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDiet, setSelectedDiet] = useState(null);
  const [autoConditions, setAutoConditions] = useState([]);   // pre-selected from visit
  const [masterclass, setMasterclass] = useState("");
  const [mcLoading, setMcLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get(`/patients/${pid}`);
        const pt = p.data.patient;
        setPatient(pt);
        const v = (p.data.visits || [])[0] || {};
        const vt = v.vitals || {};
        setProfile({
          age: pt?.age || "",
          weight_kg: vt.weight || "",
          height_cm: vt.height || "",
          waist_cm: vt.waist || "",
          sex: pt?.sex || "",
        });
        // Auto-map known conditions + diagnosis + medicines → Advanced Diet
        // taxonomy so the condition chip picker is pre-selected.
        try {
          const detected = autoDetectConditions({
            known: v.known || [],
            diagnosisPrimary: v.diagnosis?.primary || "",
            diagnosisSecondary: v.diagnosis?.secondary || "",
            medicines: v.medicines || [],
          });
          setAutoConditions(detected);
        } catch (err) { console.debug("[silent]", err); }
      } catch (e) {
        console.warn("load patient failed:", e);
        toast.error("Could not load patient");
      }
    })();
  }, [pid]);

  const metrics = useMemo(() => computeMetabolicTargets({ ...profile, activityId: prefs.activity_id }), [profile, prefs.activity_id]);
  const L = (en, mr) => (lang === "mr" ? mr : en);

  const buildPlan = async () => {
    if (!metrics) { toast.error("Fill age, weight, height first"); return; }
    setLoading(true);
    setPlanText("");
    setPlanId("");
    try {
      const r = await api.post("/diet/meal-plan", {
        patient_id: pid,
        profile,
        preferences: { ...prefs, bmi: metrics.bmi, tdee: metrics.tdee, target_calories: metrics.targetCalories },
        language: lang,
      });
      setPlanText(r.data?.plan?.plan_text || "");
      setPlanId(r.data?.plan?.plan_id || "");
      toast.success("Diet plan generated & saved");
    } catch (e) {
      console.warn("plan gen failed:", e);
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally { setLoading(false); }
  };

  const openMasterclass = async (d) => {
    setSelectedDiet(d); setMasterclass(""); setMcLoading(true);
    try {
      const r = await api.post("/diet/masterclass", { diet_id: d.id, language: lang, profile });
      setMasterclass(r.data?.masterclass || "");
    } catch (e) {
      console.warn("masterclass failed:", e);
      setMasterclass("Educational server is unreachable. Please try again.");
    } finally { setMcLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="diet-back-btn"><ArrowLeft size={14} className="mr-1" /> Back</Button>
          <div className="flex-1">
            <div className="text-xs text-slate-500 uppercase tracking-wider">{patient?.name} · {patient?.age} · {patient?.sex}</div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Salad size={20} className="text-emerald-600" /> {L("Diet Hub", "आहार केंद्र")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1.5 text-xs rounded-md border ${lang === "en" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
              onClick={() => setLang("en")}
              data-testid="diet-lang-en"
            >EN</button>
            <button
              className={`px-3 py-1.5 text-xs rounded-md border ${lang === "mr" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
              onClick={() => setLang("mr")}
              data-testid="diet-lang-mr"
            >मराठी</button>
            <button
              className={`px-3 py-1.5 text-xs rounded-md border ${lang === "hi" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
              onClick={() => setLang("hi")}
              data-testid="diet-lang-hi"
            >हिंदी</button>
          </div>
        </div>

        {/* v1 → v2 upgrade banner (shows only when the classic Planner tab is active). */}
        {tab === "planner" && (
          <div className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2.5 flex items-center justify-between gap-3" data-testid="upgrade-to-advanced-banner">
            <div className="text-[12px] text-indigo-900 leading-snug">
              <ShieldAlert size={12} className="inline text-indigo-700 mr-1 -mt-0.5" />
              <strong>Advanced Chart</strong> available — condition-aware macros (CKD, HF, TB, COPD, MASLD, …), 7-day menu with swap chips, foods-to-avoid + monitoring pre-fills, A4 PDF.
            </div>
            <Button size="sm" onClick={() => setTab("advanced")} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 shrink-0" data-testid="upgrade-to-advanced-btn">
              <Sparkles size={12} /> Regenerate as Advanced
            </Button>
          </div>
        )}

        <div className="flex gap-2 mb-6">
          <TabBtn active={tab === "planner"} onClick={() => setTab("planner")} testid="diet-tab-planner" icon={<Sparkles size={12} />}>{L("AI Diet Planner", "AI आहार नियोजक")}</TabBtn>
          <TabBtn active={tab === "advanced"} onClick={() => setTab("advanced")} testid="diet-tab-advanced" icon={<ShieldAlert size={12} />}>{L("Advanced Chart", "प्रगत चार्ट")}</TabBtn>
          <TabBtn active={tab === "debugger"} onClick={() => setTab("debugger")} testid="diet-tab-debugger" icon={<Beaker size={12} />}>{L("Diet Debugger", "डाएट डिबगर")}</TabBtn>
        </div>

        {tab === "advanced" && (
          <AdvancedDietChart
            patient={patient}
            defaultAnthro={{
              height_cm: profile.height_cm,
              weight_kg: profile.weight_kg,
              waist_cm: profile.waist_cm,
              age: profile.age,
              sex: profile.sex,
            }}
            preselectedConditions={autoConditions}
            lang={lang}
          />
        )}

        {tab === "planner" && (
          <div className="grid grid-cols-12 gap-4">
            <section className="col-span-12 lg:col-span-5 bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">{L("Metabolic Vitals", "मेटाबॉलिक जीवनसंख्या")}</div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  ["age", L("Age (Y)", "वय"), "number"],
                  ["weight_kg", L("Weight (kg)", "वजन (किलो)"), "number"],
                  ["height_cm", L("Height (cm)", "उंची (सेमी)"), "number"],
                  ["waist_cm", L("Waist (cm)", "कंबर (सेमी)"), "number"],
                ].map(([k, lbl, t]) => (
                  <div key={k}>
                    <label className="text-[9px] uppercase tracking-wider text-slate-500 block mb-0.5">{lbl}</label>
                    <Input type={t} value={profile[k]} onChange={(e) => setProfile((p) => ({ ...p, [k]: e.target.value }))} data-testid={`diet-vital-${k}`} />
                  </div>
                ))}
              </div>

              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{L("Activity level", "हालचाल पातळी")}</div>
              <div className="grid grid-cols-2 gap-1.5 mb-4">
                {ACTIVITY_LEVELS.map((al) => (
                  <button key={al.id}
                    className={`text-[10px] uppercase tracking-wider px-2 py-2 rounded border ${prefs.activity_id === al.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
                    onClick={() => setPrefs((p) => ({ ...p, activity_id: al.id }))}
                    data-testid={`diet-activity-${al.id}`}
                  >
                    {L(al.name_en, al.name_mr)}
                  </button>
                ))}
              </div>

              {metrics && (
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 mb-4 text-xs" data-testid="diet-metrics">
                  <div className="flex justify-between gap-2 mb-1">
                    <span><span className="text-slate-500 text-[9px] uppercase mr-1">BMI</span> <span className="font-semibold text-emerald-800">{metrics.bmi}</span></span>
                    <span><span className="text-slate-500 text-[9px] uppercase mr-1">{L("Maintenance", "देखभाल")}</span> {metrics.tdee} kcal</span>
                  </div>
                  <div className="text-[13px]">
                    <span className="text-slate-500 text-[9px] uppercase mr-1">{L("Clinical target", "क्लिनिकल लक्ष्य")}:</span>
                    <span className="font-bold text-emerald-800 text-base">{metrics.targetCalories}</span> kcal/day
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mb-3">
                <select value={prefs.choice} onChange={(e) => setPrefs((p) => ({ ...p, choice: e.target.value }))} className="h-9 border border-slate-200 rounded px-2 text-sm" data-testid="diet-choice">
                  <option>Vegetarian</option>
                  <option>Non-Vegetarian</option>
                  <option>Eggitarian</option>
                </select>
                <select value={prefs.diet_type} onChange={(e) => setPrefs((p) => ({ ...p, diet_type: e.target.value }))} className="h-9 border border-slate-200 rounded px-2 text-sm" data-testid="diet-type-select">
                  {DIET_TYPES.map((d) => <option key={d.id} value={d.id}>{L(d.name_en, d.name_mr)}</option>)}
                </select>
              </div>
              <Input value={prefs.likings} onChange={(e) => setPrefs((p) => ({ ...p, likings: e.target.value }))} placeholder={L("Foods the patient likes (e.g. Bhakri, dal, curd)", "आवडते पदार्थ (उदा. भाकरी, डाळ, दही)")} data-testid="diet-likings" className="mb-2" />
              <Input value={prefs.allergies} onChange={(e) => setPrefs((p) => ({ ...p, allergies: e.target.value }))} placeholder={L("Allergies / dislikes", "ॲलर्जी / नावडते पदार्थ")} data-testid="diet-allergies" className="mb-3" />

              <Button onClick={buildPlan} disabled={loading || !metrics} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="diet-build-btn">
                <Sparkles size={14} className="mr-1" /> {loading ? L("Formulating…", "तयार होत आहे…") : L("Formulate Diet Chart", "आहार तक्ता तयार करा")}
              </Button>
            </section>

            <section className="col-span-12 lg:col-span-7">
              {planText ? (
                <div className="bg-white rounded-lg border border-emerald-200 shadow-sm p-5" data-testid="diet-plan-output">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1"><Salad size={12} /> {L("Personalised meal plan", "वैयक्तिक आहार आराखडा")}</div>
                    {planId && (
                      <Button size="sm" variant="outline" onClick={() => window.open(`/diet/print/${planId}`, "_blank")} data-testid="diet-print-btn">
                        <Printer size={13} className="mr-1" /> {L("Print / Share", "प्रिंट / शेअर")}
                      </Button>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-800 font-sans">{planText}</pre>
                </div>
              ) : (
                <div className="bg-white rounded-lg border-2 border-dashed border-slate-200 h-full min-h-[380px] flex flex-col items-center justify-center text-center p-8" data-testid="diet-plan-empty">
                  <Salad size={40} className="text-slate-300 mb-3" />
                  <div className="text-slate-500 font-semibold">{L("Awaiting formulation", "आराखड्याची प्रतीक्षा")}</div>
                  <div className="text-xs text-slate-400 mt-2 max-w-xs">{L("Fill vitals + protocol on the left and generate a personalised Indian meal plan.", "डावीकडील माहिती भरून वैयक्तिक भारतीय आहार आराखडा तयार करा.")}</div>
                </div>
              )}
            </section>
          </div>
        )}

        {tab === "debugger" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DIET_TYPES.map((d) => (
              <div key={d.id} className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm" data-testid={`diet-card-${d.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen size={14} className="text-emerald-600" />
                  <div className="font-semibold text-slate-900">{L(d.name_en, d.name_mr)}</div>
                </div>
                <div className="text-[11px] text-emerald-700 mb-2">{L(d.desc_en, d.desc_mr)}</div>
                <div className="text-[11px] text-slate-600 bg-slate-50 rounded p-2 border border-slate-100 leading-relaxed">
                  <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">{L("Mechanism", "कार्यपद्धत")}</span>
                  {L(d.science_en, d.science_mr)}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-3 gap-1"
                  onClick={() => openMasterclass(d)}
                  disabled={mcLoading && selectedDiet?.id === d.id}
                  data-testid={`diet-masterclass-${d.id}`}
                >
                  <MessageSquare size={12} />
                  {mcLoading && selectedDiet?.id === d.id ? L("Educating…", "शिक्षण सुरू…") : L("Discuss with AI Educator", "AI शिक्षकाशी चर्चा करा")}
                </Button>
              </div>
            ))}
            {selectedDiet && (
              <div className="col-span-full bg-slate-900 text-white rounded-lg p-5 shadow-xl" data-testid="diet-masterclass-panel">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-semibold">{L("AI Educator", "AI शिक्षक")}</div>
                    <div className="font-semibold text-lg">{L(selectedDiet.name_en, selectedDiet.name_mr)} — {L("Masterclass", "मास्टरक्लास")}</div>
                  </div>
                  <button onClick={() => { setSelectedDiet(null); setMasterclass(""); }} className="text-white/60 hover:text-white" data-testid="diet-masterclass-close"><X size={16} /></button>
                </div>
                {mcLoading ? (
                  <div className="text-sm text-slate-400 italic">{L("Loading…", "लोड होत आहे…")}</div>
                ) : (
                  <pre className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-slate-200 font-sans">{masterclass}</pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${active ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
    >
      {icon} {children}
    </button>
  );
}
